<template>
    <AmModal
        :open="open"
        size="narrow"
        title="Экспорт в PDF"
        :subtitle="modalSubtitle"
        @close="onClose"
    >
        <!--
            Гостю экспорт запрещён: PDF-файл сохраняется в личный кабинет
            («Мои файлы»), которого у неавторизованного пользователя нет.
            Показываем понятный блок с CTA «Войти», не подсвечивая техническое
            ограничение.
        -->
        <div v-if="!isAuthenticated" class="guest-block">
            <div class="guest-block-title">Экспорт доступен после входа</div>
            <div class="guest-block-text">
                PDF сохраняется в раздел «Мои файлы» в личном кабинете. Войдите,
                чтобы сгенерировать документ по выбранным сообщениям и вернуться
                к нему в любой момент.
            </div>
            <div class="modal-actions">
                <button type="button" class="ghost-btn" @click="onClose">
                    Отмена
                </button>
                <button type="button" class="primary-btn" @click="onLogin">
                    Войти
                </button>
            </div>
        </div>

        <div v-else class="modal-form">
            <div class="field">
                <span class="field-label">Имя файла</span>
                <div class="filename-input-wrap">
                    <ChatIcon name="download" :size="14" />
                    <input
                        v-model="filenameBase"
                        type="text"
                        class="filename-input"
                        maxlength="200"
                        spellcheck="false"
                        autocomplete="off"
                        :placeholder="defaultFilenameBase"
                        @keydown.enter.prevent="onExport"
                    />
                    <span class="filename-ext">.pdf</span>
                </div>
                <div class="hint">
                    Расширение «.pdf» добавится автоматически. Файл сразу скачается
                    в «Загрузки» и продублируется в разделе «Мои файлы» — оттуда
                    его можно скачать снова или удалить.
                </div>
            </div>

            <div v-if="error" class="error-line">{{ error }}</div>

            <div class="modal-actions">
                <button type="button" class="ghost-btn" :disabled="loading" @click="onClose">
                    Отмена
                </button>
                <button
                    type="button"
                    class="primary-btn"
                    :disabled="messageIds.length === 0 || !threadExternalId || loading"
                    @click="onExport"
                >
                    <ChatIcon name="download" :size="13" />
                    {{ loading ? 'Открываем…' : 'Экспортировать в PDF' }}
                </button>
            </div>
        </div>
    </AmModal>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import AmModal from '@/components/ui/AmModal.vue'
import ChatIcon from '@/components/chat/icons/ChatIcon.vue'

const props = defineProps({
    open: { type: Boolean, default: false },
    threadTitle: { type: String, default: '' },
    threadSubtitle: { type: String, default: '' },
    // externalId треда — нужен странице /chat/print, чтобы дёрнуть /history.
    threadExternalId: { type: String, default: '' },
    // БД-id сообщений (получаем через store.getSelectedServerIds()).
    // Локальные m.id тут не подойдут — на печатной странице новый экземпляр
    // ChatMessage без нашей нумерации.
    messageIds: { type: Array, default: () => [] },
    // Экспорт — фича только для авторизованных: PDF сохраняется в личный
    // кабинет. Пропс приходит из ChatPage (см. useChatStore.state.user).
    isAuthenticated: { type: Boolean, default: false },
})

const emit = defineEmits(['close', 'exported'])
const router = useRouter()

const loading = ref(false)
const error = ref('')
// Момент открытия модалки фиксируем один раз — иначе имя файла «прыгает»
// на каждом ре-рендере (пользователь видит другое имя, чем в итоговом файле).
const openedAt = ref(new Date())

watch(() => props.open, (val) => {
    if (val) {
        error.value = ''
        openedAt.value = new Date()
    }
})

const messageWord = computed(() => plural(props.messageIds.length, ['сообщения', 'сообщений', 'сообщений']))

const modalSubtitle = computed(() => {
    if (!props.isAuthenticated) {
        return 'Файл сохраняется в личный кабинет.'
    }
    return `Выбрано ${props.messageIds.length} ${messageWord.value}. Мы сгенерируем PDF и добавим его в «Мои файлы».`
})

/**
 * Дефолтное имя (без расширения): chat_thread_YYYYMMDD_HHMMSS. Формируется
 * в локальном времени пользователя — из него ниже собираем autoFilename и им
 * же заполняем инпут при открытии.
 */
const defaultFilenameBase = computed(() => {
    const d = openedAt.value
    const pad = n => String(n).padStart(2, '0')
    const date = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}`
    const time = `${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`
    return `chat_thread_${date}_${time}`
})

// Пользователь может редактировать имя. Храним «базу» (без .pdf), чтобы
// расширение не задваивалось и не терялось при копипасте.
const filenameBase = ref('')

// При каждом открытии модалки сбрасываем поле в дефолт: экспорт — «одноразовое»
// действие, старое имя из прошлого раза только запутает.
watch(() => props.open, (val) => {
    if (val) filenameBase.value = defaultFilenameBase.value
})

/**
 * Санитайзинг имени файла. Оставляем только «безопасные» символы для
 * файловых систем и HTTP Content-Disposition. Точки внутри имени допустимы,
 * но финальную точку и пробелы обрезаем — Windows не любит имена с ними
 * на конце. Если после чистки строка пуста — используем дефолт, чтобы у
 * пользователя не создался файл «.pdf».
 */
function sanitizeFilename(raw) {
    let s = (raw || '').toString().trim()
    // Убираем управляющие и запрещённые символы. Внутри пробелы разрешены.
    // eslint-disable-next-line no-control-regex
    s = s.replace(/[\u0000-\u001F<>:"/\\|?*]+/g, '_')
    // Обрезаем финальные точки/пробелы.
    s = s.replace(/[.\s]+$/g, '')
    // Хвостовое .pdf не нужно — расширение мы добавим сами.
    s = s.replace(/\.pdf$/i, '')
    return s
}

/**
 * Итоговое имя файла — то, что уйдёт и в /chat/print?filename=, и в бэк.
 */
const autoFilename = computed(() => {
    const base = sanitizeFilename(filenameBase.value) || defaultFilenameBase.value
    return `${base}.pdf`
})

function plural(n, forms) {
    const abs = Math.abs(n) % 100
    if (abs >= 11 && abs <= 14) return forms[2]
    const last = abs % 10
    if (last === 1) return forms[0]
    if (last >= 2 && last <= 4) return forms[1]
    return forms[2]
}

function onExport() {
    if (props.messageIds.length === 0 || !props.threadExternalId) return
    error.value = ''
    loading.value = true

    const params = new URLSearchParams()
    params.set('thread', props.threadExternalId)
    params.set('ids', props.messageIds.join(','))
    // filename — источник истины: то же имя мы отправим в /api/chat/exports
    // из ChatPrintPage. Иначе появится расхождение между «Мои файлы» и тем,
    // что открылось в новой вкладке.
    params.set('filename', autoFilename.value)

    const finalTitle = (props.threadTitle || 'Экспорт диалога').slice(0, 200)
    params.set('title', finalTitle)
    if (props.threadSubtitle) params.set('subtitle', props.threadSubtitle)
    // upload=1 — сигнал странице печати: после рендера сгенерировать PDF
    // html2pdf.js и залить его на бэк. Без флага страница работает в старом
    // «open browser print dialog» режиме (пригодится для отладки).
    params.set('upload', '1')

    const url = `/chat/print?${params.toString()}`
    const win = window.open(url, '_blank', 'noopener')
    if (!win) {
        loading.value = false
        error.value = 'Не удалось открыть новую вкладку — разрешите всплывающие окна и попробуйте ещё раз.'
        return
    }

    emit('exported')
    // Не блокируем UI ожиданием окончания генерации — она идёт в отдельной
    // вкладке; пользователь может продолжать работать в чате.
    setTimeout(() => { loading.value = false }, 500)
    emit('close')
}

function onLogin() {
    emit('close')
    router.push({ name: 'signin', query: { redirect: '/chat' } }).catch(() => {})
}

function onClose() {
    if (loading.value) return
    emit('close')
}
</script>

<style scoped>
.modal-form {
    display: flex;
    flex-direction: column;
    gap: 14px;
}
.field { display: flex; flex-direction: column; gap: 6px; }
.field-label { font-size: 11.5px; font-weight: 600; color: #5f5f5f; text-transform: uppercase; letter-spacing: 0.4px; }
.filename-input-wrap {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 6px 10px; border: 1px solid #e0e0e0; border-radius: 6px;
    background: #fafafa; color: #23282C; font-size: 13px;
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    width: 100%; box-sizing: border-box;
}
.filename-input-wrap:focus-within { border-color: #23282C; background: #fff; }
.filename-input {
    flex: 1 1 auto; min-width: 0;
    border: 0; background: transparent; outline: none;
    font: inherit; color: inherit; padding: 4px 0;
}
.filename-ext {
    color: #5f5f5f; user-select: none;
}
.hint {
    font-size: 12px;
    color: #5f5f5f;
    background: #f7f7f7;
    border-radius: 6px;
    padding: 10px 12px;
    line-height: 1.45;
}
.error-line { color: #C0050F; font-size: 12px; }
.modal-actions { display: flex; justify-content: flex-end; gap: 8px; padding-top: 4px; }
.ghost-btn {
    background: transparent; border: 1px solid #e0e0e0; padding: 8px 14px;
    border-radius: 6px; font-family: inherit; font-size: 13px; color: #23282C; cursor: pointer;
}
.ghost-btn:hover:not(:disabled) { background: #f0f0f0; }
.ghost-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.primary-btn {
    display: inline-flex; align-items: center; gap: 6px;
    background: #E30613; color: white; border: 0;
    padding: 8px 14px; border-radius: 6px;
    font-family: inherit; font-size: 13px; font-weight: 600; cursor: pointer;
}
.primary-btn:hover:not(:disabled) { background: #C0050F; }
.primary-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.guest-block {
    display: flex; flex-direction: column; gap: 12px;
}
.guest-block-title {
    font-size: 14px; font-weight: 600; color: #23282C;
}
.guest-block-text {
    font-size: 13px; color: #5f5f5f; line-height: 1.5;
    background: #f7f7f7; border-radius: 6px; padding: 12px 14px;
}
</style>
